if (!customElements.get('media-gallery')) {
    customElements.define('media-gallery', class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
        }
        this.mql = window.matchMedia('(min-width: 750px)');
        
        // Initialize thumbnails if they exist
        if (this.elements.thumbnails) {
          this.initThumbnails();
        }

        // Initialize navigation buttons for thumbnail layout
        if (this.dataset.desktopLayout === 'thumbnail') {
          this.initNavigation();
        }

        if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) {
          this.removeListSemantic();
        }

        if (this.dataset.setHeight) {
          window.setTimeout(this.setHeight.bind(this));
          this.resizeObserver = new ResizeObserver(this.setHeight.bind(this));
          if (this.elements.viewer) {
            this.resizeObserver.observe(this.elements.viewer);
          }
        }
      }

      initThumbnails() {
        if (!this.elements.viewer || !this.elements.thumbnails) return;

        // Update thumbnails immediately on slide change
        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));

        // Set up thumbnail click handlers
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button, .thumbnail');
          if (button) {
            button.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              this.setActiveMedia(mediaToSwitch.dataset.target, false);
            });
          }
        });
      }

      initNavigation() {
        if (!this.elements.viewer) return;

        const prevBtn = this.elements.viewer.querySelector('.product__media-nav-button--prev');
        const nextBtn = this.elements.viewer.querySelector('.product__media-nav-button--next');
        
        if (prevBtn) {
          prevBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.navigate(-1);
          });
        }
        if (nextBtn) {
          nextBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.navigate(1);
          });
        }
      }

      navigate(direction) {
        if (!this.elements.viewer) return;
        
        // In thumbnail layout, get items from the list container
        const listContainer = this.elements.viewer.querySelector('.product__media-list--thumbnail') || 
                             this.elements.viewer.querySelector('[id^="Slider-Gallery"]');
        
        if (!listContainer) return;
        
        // Get all media items that are direct children (li elements with data-media-id)
        // Only get items that are direct children to avoid nested items
        const allMediaItems = Array.from(listContainer.children).filter(item => {
          return item.tagName === 'LI' && 
                 item.dataset && 
                 item.dataset.mediaId && 
                 item.dataset.mediaId.trim() !== '';
        });
        
        if (allMediaItems.length === 0) return;
        
        // Find the currently active item
        const activeIndex = allMediaItems.findIndex(item => item.classList.contains('is-active'));
        
        if (activeIndex === -1) {
          // If no active item, activate the first one
          if (allMediaItems[0]) {
            const mediaId = allMediaItems[0].dataset.mediaId;
            this.setActiveMedia(mediaId, false);
          }
          return;
        }

        let newIndex = activeIndex + direction;
        
        // Wrap around
        if (newIndex < 0) {
          newIndex = allMediaItems.length - 1;
        } else if (newIndex >= allMediaItems.length) {
          newIndex = 0;
        }

        const targetMedia = allMediaItems[newIndex];
        if (targetMedia && targetMedia.dataset.mediaId) {
          const mediaId = targetMedia.dataset.mediaId;
          this.setActiveMedia(mediaId, false);
        }
      }
  
      onSlideChanged(event) {
        const currentElement = event && event.detail && event.detail.currentElement;
        if (!currentElement || !this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ currentElement.dataset.mediaId }"]`);
        this.setActiveThumbnail(thumbnail);
      }

      setHeight() {
        if (!this.elements.viewer) return;
        this.style.setProperty('--height', `${this.elements.viewer.offsetHeight}px`);
      }
  
      setActiveMedia(mediaId, prepend) {
        if (!this.elements.viewer || !mediaId) return;
        
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
        if (!activeMedia) {
          console.warn('Media item not found:', mediaId);
          return;
        }

        // Verify the media item has content
        const hasContent = activeMedia.querySelector('.product__modal-opener, .product__media, img, video');
        if (!hasContent) {
          console.warn('Media item has no content:', mediaId);
          return;
        }

        // Get current active media for fade transition
        const currentActive = this.elements.viewer.querySelector('.product__media-item.is-active');
        
        // Remove active class from all media items first
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        
        // Add active class immediately for smooth transition
        // Use requestAnimationFrame to ensure smooth cross-fade
        if (currentActive && currentActive !== activeMedia) {
          // Cross-fade: start fade-out, then fade-in
          requestAnimationFrame(() => {
            activeMedia.classList.add('is-active');
          });
        } else {
          activeMedia.classList.add('is-active');
        }
  
        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }
          if (this.elements.viewer.slider && this.elements.viewer.resetPages) {
            this.elements.viewer.resetPages();
          }
        }
  
        this.preventStickyHeader();
        
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({behavior: 'smooth'});
          }
        });
        
        this.playActiveMedia(activeMedia);
  
        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
        this.setActiveThumbnail(activeThumbnail);
        
        if (activeThumbnail && activeThumbnail.dataset.mediaPosition) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }
  
      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
  
        // Remove active state from all thumbnails
        this.elements.thumbnails.querySelectorAll('button, .thumbnail').forEach((element) => {
          element.classList.remove('active');
          element.removeAttribute('aria-current');
        });
        
        // Set active state on current thumbnail
        const thumbButton = thumbnail.querySelector('button, .thumbnail');
        if (thumbButton) {
          thumbButton.classList.add('active');
          thumbButton.setAttribute('aria-current', 'true');
        }
        
        const isVertical = this.elements.thumbnails.offsetHeight > this.elements.thumbnails.offsetWidth;
        const previousThumbnail = thumbnail.previousElementSibling;
        
        if (previousThumbnail) {
          const scrollOptions = isVertical
            ? { top: (previousThumbnail || thumbnail).offsetTop - 2, behavior: 'smooth' }
            : { left: thumbnail.offsetLeft, behavior: 'smooth' };
          
          // Try to use slider scrollTo if available, otherwise use standard scrollTo
          if (this.elements.thumbnails.slider && this.elements.thumbnails.slider.scrollTo) {
            this.elements.thumbnails.slider.scrollTo(scrollOptions);
          } else if (this.elements.thumbnails.scrollTo) {
            this.elements.thumbnails.scrollTo(scrollOptions);
          } else {
            thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
          }
        }
      }
  
      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
            '[index]',
            position
          );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }
  
      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }
  
      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }
  
      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
      }
    });
  }